// src/autodesk_usage/api/corporate.ts
// All comments in English only
import { http } from "@/core/http";

export interface CorporateItem {
  name?: string | null;
  csn?: string | null;
  parent_odoo_name?: string | null;
}

export interface ChildCompanyItem {
  csn: string;
  name: string;
  used_users: number;
  active_seats: number;
}

export interface CorporateProductUsageSummary {
  usage_month: string;
  product: string;
  used_users: number;
  active_seats: number;
}

export interface CorporateMonthlyUsageRow {
  usage_month: string;
  used_users: number;
  active_seats: number;
}

export interface CompanyAccountInfo {
  name: string;
  csn: number;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  city: string | null;
  country: string | null;
  phonenumber: string | null;
  parentaccountcsn: number | null;
  website: string | null;
  named_account_group: string;
  industrysegment: string | null;
  industrysubsegment: string | null;
  customerprocurementreadiness: string | null;
  procurementrisk: string | null;
  stateprovincecode: string | null;
}

const CORPORATE_BASE = "/autodesk/customers/corporates";
const COMPANY_LIST_ENDPOINT = "/autodesk/companies";

function appendQueryParam(
  search: URLSearchParams,
  key: string,
  value?: string | string[] | null
) {
  if (Array.isArray(value)) {
    value
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((item) => search.append(key, item));
    return;
  }
  const trimmed = value?.trim();
  if (trimmed) {
    search.set(key, trimmed);
  }
}

export function getCorporateList() {
  return http.get<CorporateItem[]>(COMPANY_LIST_ENDPOINT);
}

export function getCorporateChildren(corporateName: string | string[]) {
  const search = new URLSearchParams();
  appendQueryParam(search, "corporate_name", corporateName);
  return http.get<ChildCompanyItem[]>(
    `/autodesk/corporates/children/usage?${search.toString()}`
  );
}

export function getCorporateProductUsageSummary(corporateName: string | string[]) {
  const search = new URLSearchParams();
  appendQueryParam(search, "corporate_name", corporateName);
  return http.get<CorporateProductUsageSummary[]>(
    `/autodesk/corporates/product-usage?${search.toString()}`
  );
}

export function getCorporateProductUsageRange(
  corporateName: string | string[],
  startMonth: string,
  endMonth: string
) {
  const search = new URLSearchParams();
  appendQueryParam(search, "corporate_name", corporateName);
  search.set("start_month", startMonth);
  search.set("end_month", endMonth);
  return http.get<CorporateProductUsageSummary[]>(
    `/autodesk/corporates/product-usage?${search.toString()}`
  );
}

export function getCompanyProductUsageSummary(companyName: string | string[]) {
  const search = new URLSearchParams();
  appendQueryParam(search, "company_name", companyName);
  return http.get<CorporateProductUsageSummary[]>(
    `/autodesk/companies/product-usage?${search.toString()}`
  );
}

export function getCompanyProductUsageRange(
  companyName: string | string[],
  startMonth: string,
  endMonth: string
) {
  const search = new URLSearchParams();
  appendQueryParam(search, "company_name", companyName);
  search.set("start_month", startMonth);
  search.set("end_month", endMonth);
  return http.get<CorporateProductUsageSummary[]>(
    `/autodesk/companies/product-usage?${search.toString()}`
  );
}

export function getCompanyProductUsageByMonth(
  companyName: string | string[],
  month?: string,
  startMonth?: string,
  endMonth?: string
) {
  const search = new URLSearchParams();
  appendQueryParam(search, "company_name", companyName);
  if (month) {
    search.set("month", month);
  }
  if (startMonth) {
    search.set("start_month", startMonth);
  }
  if (endMonth) {
    search.set("end_month", endMonth);
  }
  return http.get<CorporateProductUsageSummary[]>(
    `/autodesk/companies/product-usage?${search.toString()}`
  );
}

export function getCorporateMonthlyUsage(
  corporateName: string | string[],
  month?: string,
  startMonth?: string,
  endMonth?: string
) {
  const search = new URLSearchParams();
  appendQueryParam(search, "corporate_name", corporateName);
  if (month) {
    search.set("month", month);
  }
  if (startMonth) {
    search.set("start_month", startMonth);
  }
  if (endMonth) {
    search.set("end_month", endMonth);
  }
  return http.get<CorporateMonthlyUsageRow[]>(
    `/autodesk/corporates/monthly-usage?${search.toString()}`
  );
}

export function getCompanyMonthlyUsage(
  companyName: string | string[],
  startMonth?: string,
  endMonth?: string
) {
  const search = new URLSearchParams();
  appendQueryParam(search, "company_name", companyName);
  if (startMonth) {
    search.set("start_month", startMonth);
  }
  if (endMonth) {
    search.set("end_month", endMonth);
  }
  return http.get<CorporateMonthlyUsageRow[]>(
    `/autodesk/companies/monthly-usage?${search.toString()}`
  );
}

export function getCorporateProductUsageByMonth(
  corporateName: string | string[],
  month?: string,
  startMonth?: string,
  endMonth?: string
) {
  const search = new URLSearchParams();
  appendQueryParam(search, "corporate_name", corporateName);
  if (month) {
    search.set("month", month);
  }
  if (startMonth) {
    search.set("start_month", startMonth);
  }
  if (endMonth) {
    search.set("end_month", endMonth);
  }
  return http.get<CorporateProductUsageSummary[]>(
    `/autodesk/corporates/product-usage?${search.toString()}`
  );
}

export function getCompanyAccountInfo(companyName: string) {
  const search = new URLSearchParams();
  search.set("company_name", companyName);
  return http.get<CompanyAccountInfo | null>(
    `/autodesk/companies/account-info?${search.toString()}`
  );
}
